/**
 * Провайдер PostHog.
 *
 * Код перенесён 1:1 из index.js (инициализация) и analytics.js (identify /
 * reset). Поведение не меняется ни на йоту — это важно: провайдер вводится
 * ради второго инстанса, а первый не должен заметить рефакторинг.
 *
 * Наружу торчит только контракт провайдера: init / identify / reset / capture.
 * Всё posthog-специфичное (get_property, register, session_recording) живёт
 * здесь и выше не протекает.
 */
import posthog from 'posthog-js';
import { maskTextFn } from '../uiTextMask';

let ready = false;

export const key = 'posthog';

// Провайдер активен только при заданном ключе. Гейт был в index.js — не
// «если posthog выбран», а «если есть чем инициализировать»: на инстансе без
// ключа выбор провайдера в env не должен ронять приложение.
export function isConfigured() {
  return !!process.env.REACT_APP_POSTHOG_KEY;
}

export function init() {
  if (ready || !isConfigured()) return;

  posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
    api_host: process.env.REACT_APP_POSTHOG_HOST || '/ingest',
    ui_host: 'https://us.posthog.com',
    person_profiles: 'identified_only',
    // Изоляция по тенанту: по умолчанию posthog-js ставит cookie на .fixty.io
    // (родительский домен) → distinct_id/session_id шарятся между всеми
    // субдоменами (aimtex/pizzafab/pf-demo). Из-за этого в одном браузере
    // сессии разных тенантов смешиваются и PostHog показывает «чужой» адрес.
    // false → host-only cookie, каждый субдомен изолирован.
    cross_subdomain_cookie: false,
    capture_pageview: true,
    capture_pageleave: true,
    // Приватность тенантов: в записях сессий виден только текст интерфейса
    // (всё, что есть в locales/*.json), а данные тенанта — имена клиентов,
    // суммы, адреса, названия — и значения полей заменены звёздочками, даже
    // для владельца платформы. Логика — в analytics/uiTextMask.js:
    // maskTextSelector '*' закрывает всё, maskTextFn открывает только
    // известные строки интерфейса. mask_all_text для автозахвата ($el_text
    // у кликов) остаётся полным, иначе текст кликнутой строки списка утекал бы
    // в события. Разметка data-ph-capture-attribute-* — атрибуты, не текст,
    // поэтому аналитика по ней работает как прежде.
    mask_all_text: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      maskTextFn,
    },
    loaded: (ph) => {
      if (process.env.NODE_ENV === 'development') {
        ph.opt_out_capturing(); // не пишем dev-трафик в PostHog
      }
    },
  });

  ready = true;
}

// user — объект из /me (id, name, role, subdomain, organization_id, is_demo).
export function identify(user) {
  if (!ready) return;

  const orgDomain = user.subdomain ? `${user.subdomain}.fixty.io` : null;

  // Если в persistence «висит» ДРУГОЙ идентифицированный пользователь (зашли
  // под другим тенантом в том же браузере) — сбрасываем сессию, чтобы события
  // и session replay не смешивались. Аноним→логин не триггерит сброс:
  // $user_id есть только после identify.
  const prevUserId = posthog.get_property && posthog.get_property('$user_id');
  if (prevUserId && String(prevUserId) !== String(user.id)) {
    posthog.reset();
  }

  posthog.identify(String(user.id), {
    name: user.name,
    role: user.role,
    is_demo: user.is_demo ?? false,
    organization_id: user.organization_id ?? null,
    org_subdomain: user.subdomain || null,
    org_domain: orgDomain,
  });

  // Super-properties: каждое событие несёт домен и id ОРГи независимо от того,
  // какой $current_url поймала сессия.
  posthog.register({
    org_domain: orgDomain,
    organization_id: user.organization_id ?? null,
  });
}

export function reset() {
  if (!ready) return;
  posthog.reset();
}

export function capture(event, properties) {
  if (!ready) return;
  posthog.capture(event, properties);
}

// Автозахват у PostHog свой (data-ph-capture-attribute-* читает сам SDK).
// Ядро спрашивает об этом, чтобы не включить наш делегат поверх штатного и
// не получить каждый клик дважды.
export const hasNativeAutocapture = true;

// Пейджвью PostHog снимает сам (capture_pageview: true). У Метрики так нельзя,
// поэтому флаг спрашивается ядром, а не хардкодится в роутере. Метод оставлен
// для полноты контракта: ядро его не вызовет, пока флаг true.
export function pageview() {}

export const hasNativePageviews = true;
